import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Database, Download, FileUp, CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { SocietyPageHeader, EmptyState } from "../../components/shared";

const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

export default function SocietyOnboardingImport() {
  const [params] = useSearchParams();
  const tab = params.get("tab") || "import";
  const [file, setFile] = useState(null);
  const [cutoff, setCutoff] = useState("");
  const [firstMonth, setFirstMonth] = useState("");
  const [validating, setValidating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [firstBillSetup, setFirstBillSetup] = useState({ society_name: "", maintenance_rate: "" });
  const [preparing, setPreparing] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/society-onboarding/history");
      setHistory(data.items || []);
    } catch {
      /* non-blocking */
    }
  };
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => {
    api.get("/society-onboarding/first-bill-setup")
      .then(({ data }) => {
        setFirstBillSetup({
          society_name: data.society_name || "",
          maintenance_rate: data.prepared?.maintenance_rate || "",
        });
      })
      .catch(() => {});
  }, []);

  const prepareFirstBill = async () => {
    setPreparing(true);
    try {
      await api.put("/society-onboarding/first-bill-setup", firstBillSetup);
      toast.success("Maintenance charge prepared for all imported units");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not prepare the first maintenance charge");
    } finally {
      setPreparing(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/society-onboarding/template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mysocietymitra-onboarding-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template");
    }
  };

  const validate = async () => {
    if (!file) { toast.error("Choose an .xlsx file first"); return; }
    setValidating(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/society-onboarding/validate?cutoff_date=${encodeURIComponent(cutoff)}&first_billing_month=${encodeURIComponent(firstMonth)}`,
        form,
      );
      setPreview(data);
      if (data.summary.error_rows > 0) toast.warning(`${data.summary.error_rows} row(s) need fixing before import`);
      else toast.success(`${data.summary.valid_rows} row(s) ready to import`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const confirm = async () => {
    if (!preview || preview.summary.error_rows > 0) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/society-onboarding/${preview.onboarding_job_id}/confirm`, {});
      setResult(data);
      setPreview(null);
      setFile(null);
      toast.success("Import posted successfully");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setConfirming(false);
    }
  };

  const catTotal = (row) => Object.values(row.categories || {}).reduce((s, v) => s + Number(v || 0), 0) + Number(row.late_fees || 0);

  return (
    <div className="space-y-6" data-testid="onboarding-import-page">
      <SocietyPageHeader
        icon={Database}
        title={tab === "history" ? "Import History" : tab === "errors" ? "Import Error Reports" : "Import Old Data"}
        subtitle={tab === "history" ? "Every onboarding import run for this society." : tab === "errors" ? "Rows that failed validation and why." : "Onboard your existing flats, shops, owners and category-wise outstanding from one Excel file."}
      />

      {tab === "import" && (<>
      <div className="border border-sky-200 bg-sky-50 p-6" data-testid="onboarding-first-bill-setup">
        <h3 className="font-display text-base font-bold text-sky-950">First bill setup</h3>
        <p className="mt-1 text-sm text-slate-600">
          Confirm the registered society name and set one fixed monthly maintenance rate.
          This prepares Maintenance for every unit before the first bill run.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Society name</label>
            <Input
              data-testid="onboarding-society-name"
              onChange={(event) => setFirstBillSetup({ ...firstBillSetup, society_name: event.target.value })}
              value={firstBillSetup.society_name}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Monthly maintenance rate (₹)</label>
            <Input
              data-testid="onboarding-maintenance-rate"
              min="0.01"
              onChange={(event) => setFirstBillSetup({ ...firstBillSetup, maintenance_rate: event.target.value })}
              step="0.01"
              type="number"
              value={firstBillSetup.maintenance_rate}
            />
          </div>
        </div>
        <Button
          className="mt-4"
          data-testid="onboarding-prepare-first-bill"
          disabled={preparing}
          onClick={prepareFirstBill}
        >
          {preparing ? "Preparing…" : "Prepare maintenance charge"}
        </Button>
        <a
          className="ml-3 text-sm font-semibold text-sky-800 underline-offset-4 hover:underline"
          data-testid="onboarding-payment-settings-link"
          href="/app/finance?tab=settings"
        >
          Add UPI Pay QR in Bill Settings
        </a>
      </div>
      {/* Step 1 — template + settings */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-bold text-[#0F2B63]">1. Prepare your file</h3>
            <p className="text-sm text-slate-500">Download the 23-column template, fill your records, keep the sample row only as a guide.</p>
          </div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="onboarding-download-template">
            <Download className="h-4 w-4" /> Download template
          </Button>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Financial cut-off date (last outstanding date)</label>
            <Input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} data-testid="onboarding-cutoff-date" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">First billing month</label>
            <Input type="month" value={firstMonth} onChange={(e) => setFirstMonth(e.target.value)} data-testid="onboarding-first-month" />
          </div>
        </div>
      </div>

      {/* Step 2 — upload + validate */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="font-display text-base font-bold text-[#0F2B63]">2. Upload & validate</h3>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-2 text-sm text-slate-600 hover:border-[#2563EB]">
            <FileUp className="h-4 w-4" />
            <span data-testid="onboarding-file-name">{file ? file.name : "Choose .xlsx file"}</span>
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); setResult(null); }} data-testid="onboarding-file-input" />
          </label>
          <Button onClick={validate} disabled={!file || validating} data-testid="onboarding-validate-btn">
            {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Validate
          </Button>
        </div>
      </div>

      {/* Step 3 — preview */}
      {preview && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="onboarding-preview">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-base font-bold text-[#0F2B63]">3. Preview & confirm</h3>
            <div className="flex items-center gap-4 text-sm">
              <span className="font-semibold text-emerald-600" data-testid="onboarding-valid-count">{preview.summary.valid_rows} valid</span>
              <span className={preview.summary.error_rows ? "font-semibold text-rose-600" : "text-slate-400"} data-testid="onboarding-error-count">{preview.summary.error_rows} errors</span>
              <Button onClick={confirm} disabled={preview.summary.error_rows > 0 || confirming} data-testid="onboarding-confirm-btn">
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm import
              </Button>
            </div>
          </div>

          {preview.errors?.length > 0 && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="onboarding-errors">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-rose-700"><AlertTriangle className="h-4 w-4" /> Fix these rows before importing</div>
              <ul className="space-y-1 text-xs text-rose-700">
                {preview.errors.map((e, i) => (
                  <li key={i}>Row {e.row}{e.unit_number ? ` (${e.unit_number})` : ""}: {e.errors.join("; ")}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#0F2B63] text-xs uppercase tracking-wide text-white">
                <tr>
                  {["Unit", "Type", "Primary Owner", "Joint Owner", "Maint.", "NOC", "Parking", "Contrib.", "Other", "Late/Int.", "Total"].map((h) => (
                    <th key={h} className="px-3 py-2 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50/60" : "bg-white"} data-testid={`onboarding-row-${r.unit_number}`}>
                    <td className="px-3 py-2 font-medium text-[#0F2B63]">{r.building} {r.wing} {r.unit_number}</td>
                    <td className="px-3 py-2">{r.unit_type}</td>
                    <td className="px-3 py-2">{r.primary_owner?.name || "—"}</td>
                    <td className="px-3 py-2">{r.joint_owner?.name || "—"}</td>
                    <td className="px-3 py-2">{money(r.categories?.MAINT)}</td>
                    <td className="px-3 py-2">{money(r.categories?.NOC)}</td>
                    <td className="px-3 py-2">{money(r.categories?.PARKING)}</td>
                    <td className="px-3 py-2">{money(r.categories?.CONTRIB)}</td>
                    <td className="px-3 py-2">{money(r.categories?.OTHER)}</td>
                    <td className="px-3 py-2">{money(r.late_fees)}</td>
                    <td className="px-3 py-2 font-semibold">{money(catTotal(r))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 4 — result report */}
      {result && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 shadow-sm" data-testid="onboarding-result">
          <div className="mb-3 flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-5 w-5" /><span className="font-display text-base font-bold">Import completed</span></div>
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
            {Object.entries(result.counts).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-white px-3 py-2 ring-1 ring-emerald-100">
                <div className="text-lg font-bold text-[#0F2B63]">{v}</div>
                <div className="text-xs capitalize text-slate-500">{k.replaceAll("_", " ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}

      {tab === "errors" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="onboarding-error-reports">
          {history.filter((h) => (h.errors || []).length).length === 0 ? (
            <EmptyState icon={AlertTriangle} title="No import errors recorded" />
          ) : (
            history.filter((h) => (h.errors || []).length).map((h) => (
              <div key={h.onboarding_job_id} className="mb-4">
                <div className="mb-1 text-sm font-semibold text-[#0F2B63]">{h.filename} · {(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                <ul className="space-y-1 text-xs text-rose-700">
                  {h.errors.map((e, i) => (<li key={i}>Row {e.row}{e.unit_number ? ` (${e.unit_number})` : ""}: {e.errors.join("; ")}</li>))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}

      {/* Import history */}
      {tab !== "errors" && (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="onboarding-history">
        <h3 className="mb-3 font-display text-base font-bold text-[#0F2B63]">Import history</h3>
        {history.length === 0 ? (
          <EmptyState icon={Database} title="No imports yet" />
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.onboarding_job_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-2 text-sm">
                <span className="font-medium text-slate-700">{h.filename}</span>
                <span className="text-xs text-slate-500">{(h.created_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${h.status === "CONFIRMED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{h.status}</span>
                {h.counts && <span className="text-xs text-slate-500">{h.counts.units_created} units · {h.counts.members_created} members · {h.counts.opening_posted} opening</span>}
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
